import React, { useState } from 'react';
import { Send, Mail, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Reveal } from './Reveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    package: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);

      toast.success("Message Sent Successfully!", {
        description: "We'll get back to you within 24 hours.",
      });

      setFormData({ name: '', email: '', phone: '', package: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error("Error Sending Message", {
        description: "Please try again or contact us directly at gotomediacollective@gmail.com",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'gotomediacollective@gmail.com', href: 'mailto:gotomediacollective@gmail.com' },
    { icon: Phone, label: 'Phone', value: '(864) 280-6726', href: 'tel:+18642806726' },
    { icon: MapPin, label: 'Location', value: 'Seneca, South Carolina', href: null }
  ];

  return (
    <section id="contact" className="section-padding bg-[#050505] border-t border-white/5" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="text-center max-w-3xl mx-auto mb-16">
          <p className="overline mb-5">Get In Touch</p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6">
            Let&apos;s make you <span className="gold-gradient">famous.</span>
          </h2>
          <p className="text-lg md:text-xl text-[#a0a0a0] leading-relaxed">
            Ready to elevate your content? Let&apos;s discuss your project and bring your vision to life.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Form */}
          <Reveal className="lg:col-span-3">
            <div className="bg-[#0f0f0f] rounded-3xl p-8 md:p-10 border border-white/5">
              <h3 className="font-display text-2xl font-semibold mb-8 text-white">Send us a message</h3>
              <form onSubmit={handleSubmit} className="space-y-7" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label htmlFor="name" className="block text-xs uppercase tracking-[0.15em] text-[#a0a0a0] mb-3">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      data-testid="contact-name-input"
                      className="input-underline"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs uppercase tracking-[0.15em] text-[#a0a0a0] mb-3">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="contact-email-input"
                      className="input-underline"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label htmlFor="phone" className="block text-xs uppercase tracking-[0.15em] text-[#a0a0a0] mb-3">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="contact-phone-input"
                      className="input-underline"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label htmlFor="package" className="block text-xs uppercase tracking-[0.15em] text-[#a0a0a0] mb-3">
                      Interested Package
                    </label>
                    <select
                      id="package"
                      name="package"
                      value={formData.package}
                      onChange={handleChange}
                      data-testid="contact-package-select"
                      className="input-underline"
                    >
                      <option value="">Select a package</option>
                      <option value="exposure">Exposure - $750</option>
                      <option value="over-exposed">Over Exposed - $1,250</option>
                      <option value="celebrity">Celebrity - $2,500</option>
                      <option value="custom">Custom Package</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs uppercase tracking-[0.15em] text-[#a0a0a0] mb-3">
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    data-testid="contact-message-input"
                    className="input-underline resize-none"
                    placeholder="Tell us about your project..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-button"
                  className="cta-button w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send size={18} />
                </button>
              </form>
            </div>
          </Reveal>

          {/* Contact Info */}
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="h-full bg-[#0f0f0f] rounded-3xl p-8 md:p-10 border border-white/5">
              <h3 className="font-display text-2xl font-semibold mb-8 text-white">Contact Information</h3>
              <div className="space-y-8">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#d4af37]/15 flex items-center justify-center flex-shrink-0">
                        <Icon size={22} className="text-[#d4af37]" strokeWidth={1.5} />
                      </div>
                      <div>
                        <div className="text-[#a0a0a0] text-xs uppercase tracking-[0.15em] mb-1.5">{info.label}</div>
                        {info.href ? (
                          <a href={info.href} className="text-white hover:text-[#d4af37] transition-colors break-all">
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-white">{info.value}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-10 pt-8 border-t border-white/5">
                <p className="text-[#a0a0a0] leading-relaxed">
                  Prefer to talk it through? Reach out and our team will help you pick the perfect package
                  for your goals.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
